import React, { useEffect, useRef, useState } from "react";
import { useMutation, useLazyQuery } from "@apollo/client";
import classnames from "classnames";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { addError, stopDurationVital, startDurationVital, addAction } from "@jsq/observability";
import { useDashboardHub } from "../../contexts/DashboardHubContext";
import { Viewer } from "../../contexts/types";
import { DATA_SOURCES, NEW_DASHBOARD_IMAGE, REDSHIFT_DATA_SOURCES } from "./constants";
import { UPSERT_DASHBOARD } from "./graphql/mutations";
import { GENERATE_PRESIGNED_URL } from "./graphql/queries";
import {
  UpsertDashboardInput,
  UpsertDashboardResponse,
  GeneratePresignedUrlInput,
  UploadUrlResponse,
} from "./graphql/types";
import { uploadFile } from "./s3Upload";
import * as styles from "./styles_vanilla.css";
import type { RevealViewProps, RVDashboard } from "./types";

const DD_VITAL_NAME = "RevealView";

export const RevealView: React.FC<RevealViewProps> = ({
  dashboard,
  height = 800,
  width = "100%",
  showFilters = false,
  canEdit = false,
  canSave = false,
  type,
  crosshairs = false,
  className,
  createNewDashboard = false,
  store,
  autoSelectFilterList = [],
  onUrlLinkRequested,
  showChangeVisualization = true,
  startInEditMode = false,
  onSaveCompleted,
}) => {
  // Parse dashboard to a number if it's a string containing only digits
  const dashboardId = /^\d+$/.test(dashboard) ? parseInt(dashboard, 10) : dashboard;
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isLoaded, arenaId } = useDashboardHub();

  const [getPresignedUrl, { error: presignedUrlError }] = useLazyQuery<
    { getS3UploadUrlForDashboard: UploadUrlResponse },
    { objectInput: GeneratePresignedUrlInput }
  >(GENERATE_PRESIGNED_URL);

  const [executeDashboardMutation] = useMutation<
    { upsertDashboard: UpsertDashboardResponse },
    { objectInput: UpsertDashboardInput }
  >(UPSERT_DASHBOARD);

  /**
   * Save a dashboard to the server using S3 for file upload
   *
   * @param name - The name of the dashboard
   * @param bytes - The dashboard file as an ArrayBuffer
   * @param isSaveAs - Whether this is a "Save As" operation
   * @returns The id of the created dashboard
   */
  async function saveDashboard(
    name: string,
    bytes: ArrayBuffer,
    // eslint-disable-next-line unused-imports/no-unused-vars,@typescript-eslint/no-unused-vars
    isSaveAs: boolean = false,
  ): Promise<string | undefined> {
    // Create a File object from the ArrayBuffer
    const file = new File([bytes], `${name}.rdash`, { type: "application/octet-stream" });

    // Step 1: Get a pre-signed URL for S3 upload using the GraphQL query
    const parsedArenaId = parseInt(arenaId, 10);
    const presignedUrlResult = await getPresignedUrl({
      variables: {
        objectInput: {
          filename: file.name,
          arenaId: parsedArenaId,
        },
      },
    });
    if (presignedUrlError) {
      throw new Error(`Failed to get S3 upload URL: ${presignedUrlError.message}`);
    }

    // Parse the response from the GraphQL query
    const responseData = presignedUrlResult.data?.getS3UploadUrlForDashboard;
    if (!responseData) {
      throw new Error("Failed to get S3 upload URL: No data returned");
    }

    const objectKey = responseData.objectKey;
    if (!objectKey) {
      throw new Error("Failed to get S3 upload URL: Missing objectKey");
    }

    // Step 2: Upload the file to S3
    const s3UploadFile = {
      generatedUrlObj: {
        objectKey,
        url: responseData.url,
        fields: responseData.fields,
      },
      file,
    };
    const uploadFileResult = await uploadFile(s3UploadFile, 2);
    if (!uploadFileResult.response.ok) {
      throw new Error(`Failed to upload file to S3: ${uploadFileResult.response.statusText}`);
    }

    // Step 3: Store new dashboard in dashboard-hub-be
    const result = await executeDashboardMutation({
      variables: {
        objectInput: {
          id:
            typeof dashboardId === "number"
              ? dashboardId
              : parseInt(dashboardId, 10) || undefined,
          arenaId: parsedArenaId,
          name,
          s3ObjectKey: objectKey,
          requiredRoles: ["fmr"], // Hard-coded for now, only fundraising data is available
        },
      },
    });

    if (result.errors) {
      throw new Error(`Failed to save dashboard: ${result.errors[0].message}`);
    }

    return result.data?.upsertDashboard.id;
  }

  // Initialize the RevealView component
  const initializeDashboard = (dash: unknown) => {
    if (!window.$ || !window.$.ig) return;
    const viewer: Viewer = new window.$.ig.RevealView("#revealView");
    viewerRef.current = viewer;
    // Set the dashboard empty state image, title, and subtitle
    viewer.assets.dashboardEmptyState = new window.$.ig.RVImageAsset(
      new window.$.ig.RVImage(NEW_DASHBOARD_IMAGE.src, NEW_DASHBOARD_IMAGE.alt),
      NEW_DASHBOARD_IMAGE.title,
      NEW_DASHBOARD_IMAGE.subtitle,
    );
    // Disable export and save as features
    viewer.showExportImage = false;
    viewer.showExportToPDF = false;
    viewer.showExportToExcel = false;
    viewer.showExportToPowerPoint = false;
    viewer.canSaveAs = false;
    viewer.showStatisticalFunctions = false;
    // Disable background color change
    viewer.canChangeVisualizationBackgroundColor = false;
    viewer.showRefresh = false;
    // If in create mode, create a new dashboard
    viewer.dashboard = createNewDashboard ? new window.$.ig.RVDashboard() : dash;
    viewer.singleVisualizationMode = type === "view";
    viewer.showFilters = showFilters;
    viewer.canEdit = canEdit;
    viewer.canSave = canSave;
    viewer.serverSideSave = false;
    viewer.crosshairsEnabled = crosshairs;
    viewer.showChangeVisualization = showChangeVisualization;
    viewer.startInEditMode = startInEditMode;
    // Disable menu if canEdit is false
    viewer.showMenu = canEdit || false;

    const viewerDashboard = viewer.dashboard as RVDashboard;
    // Loop through the filters in autoSelectFilter
    for (const filter of viewerDashboard.filters) {
      const autoSelectFilter = autoSelectFilterList.find((f) => f.field === filter.title);
      if (autoSelectFilter) {
        // If given a title, auto select the label from that title or use the first value of the filters
        filter.getFilterValues((filterValues) => {
          const title = filterValues.find((v) => v.label === autoSelectFilter.title);
          filter.selectedValues = [title?.label ?? filterValues[0].label];
        });
      }
    }
    // Add data source
    viewer.onDataSourcesRequested = (callback) => {
      if (!window.$?.ig) return;
      const dataSource = new window.$.ig.RVRESTDataSource();
      dataSource.id = DATA_SOURCES[store].id;
      dataSource.title = DATA_SOURCES[store].title;
      dataSource.subtitle = DATA_SOURCES[store].subtitle;
      dataSource.useAnonymousAuthentication = true;

      // Redshift data source
      const rsDataSource = new window.$.ig.RVRedshiftDataSource();
      rsDataSource.id = REDSHIFT_DATA_SOURCES[store].id;
      rsDataSource.title = REDSHIFT_DATA_SOURCES[store].title;
      rsDataSource.subtitle = REDSHIFT_DATA_SOURCES[store].subtitle;
      const rsDataSourceItem = new window.$.ig.RVRedshiftDataSourceItem(rsDataSource);
      rsDataSourceItem.id = REDSHIFT_DATA_SOURCES[store].id;
      rsDataSourceItem.title = REDSHIFT_DATA_SOURCES[store].title;
      rsDataSourceItem.subtitle = REDSHIFT_DATA_SOURCES[store].subtitle;
      callback(new window.$.ig.RevealDataSources([dataSource], [rsDataSourceItem], false));
    };

    viewer.onUrlLinkRequested = (args) => {
      // add each filter value to selectedValues because the callback may need additional field values
      const selectedValues = viewerDashboard.filters.map((filter) => ({
        title: filter.title,
        selectedValues: filter.selectedValues,
      }));
      const url = onUrlLinkRequested?.(args, selectedValues) || args.url;
      // DD Log for link click from RevealView
      addAction("navigateFromRevealView", {
        dashboard: dashboardId,
        url,
      });
      return url;
    };

    // Custom save logic
    viewer.onSave = (rv, args) => {
      if (args.saveAs) {
        const newName = prompt("Please enter the dashboard name");
        if (!newName) return;
        args.dashboardId = args.name = newName;
        args.serializeWithNewName(newName, (bytes: ArrayBuffer) => {
          void saveDashboard(newName, bytes, true).then((id) => {
            args.saveFinished();
            onSaveCompleted?.(id);
          });
        });
      } else {
        args.serialize((bytes: ArrayBuffer) => {
          void saveDashboard(args.name, bytes).then((id) => {
            args.saveFinished();
            onSaveCompleted?.(id);
          });
        });
      }
    };

    setIsLoading(false);
    stopDurationVital(DD_VITAL_NAME);
  };

  // Handle errors when loading the dashboard
  const handleError = (err: Error, error?: string) => {
    addError(err.message, { component: "RevealView", dashboard: dashboardId, error });
    setError(err.message);
    stopDurationVital(DD_VITAL_NAME);
    setIsLoading(false);
  };

  // Load the dashboard on mount and when the dashboard prop changes
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    let mounted = true;

    const loadDashboard = () => {
      try {
        if (!isLoaded) return;
        if (!window.$ || !window.$.ig) return;
        startDurationVital(DD_VITAL_NAME, {
          description: "Load Reveal dashboard",
          context: { dashboard: dashboardId },
        });
        if (createNewDashboard) {
          const newDashboard = new window.$.ig.RVDashboard();
          if (!mounted) return;
          initializeDashboard(newDashboard);
        } else {
          window.$?.ig?.RVDashboard.loadDashboard(
            // Ensure dashboardId is a string for loadDashboard
            typeof dashboardId === "string" ? dashboardId : String(dashboardId),
            (dash) => {
              if (!mounted) return;
              void initializeDashboard(dash);
            },
            (error) => {
              if (!mounted) return;
              handleError(new Error("Failed to load dashboard"), error);
            },
          );
        }
      } catch (err) {
        if (!mounted) return;
        handleError(err instanceof Error ? err : new Error("Failed to initialize Reveal"));
      }
    };
    loadDashboard();
    return () => {
      mounted = false;
      if (viewerRef.current) {
        viewerRef.current = undefined;
      }
    };
  }, [dashboardId, isLoaded]);

  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.singleVisualizationMode = type === "view";
    viewer.showFilters = showFilters;
    viewer.canEdit = canEdit;
    viewer.canSave = canSave;
    viewer.crosshairsEnabled = crosshairs;
  }, [showFilters, canEdit, canSave, type, crosshairs]);

  return (
    <>
      {isLoading && <LoadingIndicator />}
      {error && <ErrorMessage>{error}</ErrorMessage>}
      <div
        id="revealView"
        ref={containerRef}
        data-testid="revealView"
        className={classnames(styles.revealView, className)}
        style={{
          display: isLoading || error ? "none" : "block",
          height,
          width,
        }}
      />
    </>
  );
};

export default RevealView;
